import axios from 'axios';

const BASE_URL = 'https://api.dpsatta.com/api'; // live   
// const BASE_URL = 'http://localhost:5001/api'; // local 

// Create axios instance
const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
}); 

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  signup: (userData) => api.post('/auth/signup', userData),
};

export const userAPI = {
  getProfile: () => api.get('/profile/profile'),
  updateProfile: (data) => api.put('/profile/profile', data),
  updatePassword: (passwordData) => api.put('/user/update-password', passwordData),
  listUsers: () => api.get('/user/list'),
  getMyWallet: () => api.get('/user/wallet'),
  createUser: (userData) => api.post('/user/create', userData),
  createPlayerUser: (userData) => api.post('/user/create-player', userData),
  playerSignup: (userData) => api.post('/user/player-signup', userData),
  getUserDetails: (id) => api.get(`/user/details/${id}`),
  toggleUserStatus: (id) => api.put(`/user/toggle-status/${id}`),
  getUserTree: (id) => api.get(`/user/user-tree/${id}`),
  toggleUserTreeStatus: (id, action) => api.put(`/user/tree-status/${id}`, { action }),
};

export const walletAPI = {
  transfer: (transferData) => api.post('/wallet/transfer', transferData),
  getTransactions: () => api.get('/wallet/transactions'),
  approveWithdrawal: (transactionId) => api.put(`/wallet/withdraw/approve/${transactionId}`),
  rejectWithdrawal: (transactionId) => api.put(`/wallet/withdraw/reject/${transactionId}`),
  getWithdrawals: () => api.get('/wallet/withdrawals'),
  updateWalletBalance: (userId, walletData) => api.put(`/user/wallet/${userId}`, walletData),
  
  // Recharge APIs
  approveRecharge: (transactionId, action) => api.put(`/wallet/recharge/approve/${transactionId}`, { action }),
  getRechargeHistory: (params) => api.get('/wallet/recharge/history', { params }),
  getUserRechargeHistory: (userId, params) => api.get(`/wallet/recharge/history/${userId}`, { params }),
};

export const notificationAPI = {
  getNotifications: () => api.get('/notifications/'),
  markRead: (id) => api.post(`/notifications/read/${id}`),
};

export const marketAPI = {
  getMarkets: () => api.get('/market/'),
  getMarketsResults: (date) => api.get('/market/getmarketsresults', { params: { date } }),
  createMarket: (marketData) => api.post('/market/', marketData),
  getMarketById: (id) => api.get(`/market/${id}`),
  updateMarket: (id, marketData) => api.put(`/market/${id}`, marketData),
  deleteMarket: (id) => api.delete(`/market/${id}`),
  updateMarketResults: (id, results) => api.put(`/market/${id}/results`, results),
};

export const galiAPI = {
  // Markets
  getGaliMarkets: () => api.get('/gali/markets'),
  createGaliMarket: (marketData) => api.post('/gali/markets', marketData),
  updateGaliMarket: (id, marketData) => api.put(`/gali/markets/${id}`, marketData),
  deleteGaliMarket: (id) => api.delete(`/gali/markets/${id}`),

  // Game Rates
  getGaliGameRates: () => api.get('/gali/game-rates'),
  createGaliGameRate: (rateData) => api.post('/gali/game-rates', rateData),
  updateGaliGameRate: (id, rateData) => api.put(`/gali/game-rates/${id}`, rateData),
  deleteGaliGameRate: (id) => api.delete(`/gali/game-rates/${id}`),

  // Bets
  getGaliBets: (params) => api.get('/gali/bets', { params }),
  getGaliBetById: (id) => api.get(`/gali/bets/${id}`),
  updateGaliBetStatus: (id, statusData) => api.put(`/gali/bets/${id}/status`, statusData),

  // Results
  getGaliResults: (params) => api.get('/gali/results', { params }),
  declareGaliResult: (resultData) => api.post('/gali/results', resultData),
  updateGaliResult: (id, resultData) => api.put(`/gali/results/${id}`, resultData),
  deleteGaliResult: (id) => api.delete(`/gali/results/${id}`),
  previewGaliWinners: (previewData) => api.post('/gali/results/preview', previewData),
};

export const matkaAPI = {
  // Game Rates
  getMatkaRates: () => api.get('/matka/rates'),
  createMatkaRate: (rateData) => api.post('/matka/rates', rateData),
  updateMatkaRate: (id, rateData) => api.put(`/matka/rates/${id}`, rateData),
  deleteMatkaRate: (id) => api.delete(`/matka/rates/${id}`),

  // Bets
  placeMatkaBet: (marketId, betData) => api.post(`/matka/bet/${marketId}`, betData),
  getMatkaBets: (params) => api.get('/matka/bets', { params }),
  getMatkaBetById: (id) => api.get(`/matka/bet/${id}`),

  // Results
  declareMatkaResult: (marketId, resultData) => api.post(`/matka/result/${marketId}`, resultData),
  previewMatkaWinners: (marketId, previewData) => api.post(`/matka/result/preview/${marketId}`, previewData),
  getMatkaResults: (params) => api.get('/matka/results', { params }),
  getMatkaStatistics: (params) => api.get('/matka/statistics', { params }),
  deleteMatkaResult: (id) => api.delete(`/matka/result/${id}`), // Add this

};

export const noticeboardAPI = {
  // Public routes
  getActiveNotices: () => api.get('/noticeboard/active'),
  
  // Admin routes
  createNotice: (noticeData) => api.post('/noticeboard/', noticeData),
  getAllNotices: () => api.get('/noticeboard/all'),
  updateNotice: (id, noticeData) => api.put(`/noticeboard/${id}`, noticeData),
  deleteNotice: (id) => api.delete(`/noticeboard/${id}`),
};

export const howToPlayAPI = {
  // Public routes
  getHowToPlay: (gameType) => api.get('/how-to-play/', { params: { game_type: gameType } }),
  
  // Admin routes
  createHowToPlay: (howToPlayData) => api.post('/how-to-play/', howToPlayData),
  getAllHowToPlay: () => api.get('/how-to-play/all'),
  updateHowToPlay: (id, howToPlayData) => api.put(`/how-to-play/${id}`, howToPlayData),
  deleteHowToPlay: (id) => api.delete(`/how-to-play/${id}`),
};

export const pushNotificationAPI = {
  // User routes
  getUserNotifications: (params) => api.get('/push-notifications/user', { params }),
  
  // Admin routes
  sendPushNotification: (notificationData) => api.post('/push-notifications/send', notificationData),
  getAllNotifications: (params) => api.get('/push-notifications/all', { params }),
  getNotificationStats: () => api.get('/push-notifications/stats'),
  deleteNotification: (id) => api.delete(`/push-notifications/${id}`),
};

export const merchantUPIAPI = {
  // Public routes
  getMerchantUPIs: (params) => api.get('/merchant-upi/', { params }),
  getPrimaryUPI: () => api.get('/merchant-upi/primary'),
  getMerchantUPIById: (id) => api.get(`/merchant-upi/${id}`),
  
  // Admin routes - with FormData support for QR code uploads
  createMerchantUPI: (formData) => api.post('/merchant-upi/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  updateMerchantUPI: (id, formData) => api.put(`/merchant-upi/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  deleteMerchantUPI: (id) => api.delete(`/merchant-upi/${id}`),
  toggleUPIStatus: (id) => api.patch(`/merchant-upi/${id}/toggle-status`),
  setPrimaryUPI: (id) => api.patch(`/merchant-upi/${id}/set-primary`),
};

export const bannerAPI = {
  getBanners: (params) => api.get('/banners/', { params }),
  getBannerById: (id) => api.get(`/banners/${id}`),
  createBanner: (formData) => api.post('/banners/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  updateBanner: (id, formData) => api.put(`/banners/${id}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteBanner: (id) => api.delete(`/banners/${id}`),
  toggleBannerStatus: (id) => api.patch(`/banners/${id}/toggle-status`),
};

export const adminSettingsAPI = {
  // Admin panel routes
  getAdminSettings: () => api.get('/admin-settings/'),
  updateAdminSettings: (settingsData) => api.put('/admin-settings/', settingsData),
  
  // Public limit endpoints
  getBettingLimits: () => api.get('/admin-settings/betting-limits'),
  getWithdrawalLimits: () => api.get('/admin-settings/withdrawal-limits'),
  getDepositLimits: () => api.get('/admin-settings/deposit-limits'),
  
  // App maintenance endpoints
  getAppStatus: () => api.get('/admin-settings/app-status'),
  toggleAppMaintenance: (maintenanceData) => api.put('/admin-settings/app-maintenance', maintenanceData),
};

export const reportsAPI = {
  // Date-wise summary report
  getDateWiseSummary: (params) => api.get('/reports/date-summary', { params }),
  
  // Quick stats overview
  getQuickStats: () => api.get('/reports/quick-stats'),
  
  // Market activity reports
  getGaliMarketActivity: (params) => api.get('/reports/gali-market-activity', { params }),
  getMatkaMarketActivity: (params) => api.get('/reports/matka-market-activity', { params }),
  
  // Game activity reports (Aviator, Chicken Road & 7 Up Down)
  getAviatorActivity: (params) => api.get('/reports/aviator-activity', { params }),
  getChickenRoadActivity: (params) => api.get('/reports/chicken-road-activity', { params }),
  getSevenUpDownActivity: (params) => api.get('/reports/seven-up-down-activity', { params }),
  
  // Available markets for filtering
  getAvailableMarkets: () => api.get('/reports/available-markets'),
  
  // Dashboard overview
  getDashboardOverview: () => api.get('/reports/dashboard-overview'),
};

export const aviatorAPI = {
  // Public routes (no auth required)
  getCurrentRound: () => api.get('/aviator/current-round'),
  getRoundHistory: (params) => api.get('/aviator/round-history', { params }),
  getRoundDetails: (roundId) => api.get(`/aviator/round/${roundId}`),
  verifyFairness: (roundId) => api.get(`/aviator/verify/${roundId}`),
  getRecentWinners: (params) => api.get('/aviator/recent-winners', { params }),
  
  // User routes (auth required)
  getMyBets: (params) => api.get('/aviator/my-bets', { params }),
  
  // Admin routes (owner/admin only)
  getStatistics: (params) => api.get('/aviator/statistics', { params }),
  getAllBets: (params) => api.get('/aviator/all-bets', { params }),
  
  // Settings routes (owner only)
  getSettings: () => api.get('/aviator/settings'),
  updateSettings: (settingsData) => api.put('/aviator/settings', settingsData),
  toggleGameStatus: (statusData) => api.post('/aviator/settings/toggle-status', statusData),
};

export const chickenRoadAPI = {
  // Public routes (no auth required)
  getConfig: () => api.get('/chicken-road/config'),
  verifySession: (sessionId) => api.get(`/chicken-road/verify/${sessionId}`),
  getRecentWinners: (params) => api.get('/chicken-road/recent-winners', { params }),
  
  // User routes (auth required) - History
  getHistory: (params) => api.get('/chicken-road/history', { params }),
  getMyBets: (params) => api.get('/chicken-road/my-bets', { params }),
  
  // Admin routes (owner/admin only)
  getStatistics: (params) => api.get('/chicken-road/statistics', { params }),
  getAllSessions: (params) => api.get('/chicken-road/all-sessions', { params }),
  
  // Settings routes (owner only)
  getSettings: () => api.get('/chicken-road/settings'),
  updateSettings: (settingsData) => api.put('/chicken-road/settings', settingsData),
  toggleGameStatus: (statusData) => api.post('/chicken-road/settings/toggle-status', statusData),
};

export const sevenUpDownAPI = {
  // Public routes (no auth required)
  getCurrentRound: () => api.get('/seven-up-down/current-round'),
  getRoundHistory: (params) => api.get('/seven-up-down/round-history', { params }),
  getRoundDetails: (roundId) => api.get(`/seven-up-down/round/${roundId}`),
  getRecentResults: (params) => api.get('/seven-up-down/recent-results', { params }),
  getSettings: () => api.get('/seven-up-down/settings'),
  
  // User routes (auth required)
  placeBet: (betData) => api.post('/seven-up-down/bet', betData),
  getMyBets: (params) => api.get('/seven-up-down/my-bets', { params }),
  getMyStatistics: (params) => api.get('/seven-up-down/my-statistics', { params }),
  getLeaderboard: (params) => api.get('/seven-up-down/leaderboard', { params }),
  
  // Admin routes (admin only)
  updateSettings: (settingsData) => api.put('/seven-up-down/settings', settingsData),
  getAdminRoundBets: (roundId) => api.get(`/seven-up-down/admin/round/${roundId}/bets`),
  getAdminAnalytics: (params) => api.get('/seven-up-down/admin/analytics', { params }),
  getAllBets: (params) => api.get('/seven-up-down/admin/all-bets', { params }),
};

export default api;
